import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { fabric } from 'fabric';
import { TwoDMesh, actions as builderActions } from "@/store/builderSlice";
import { findEndpoints, intersectOfLines } from "@/utils/builder/twoDRoomLayoutUtils";
import { Point } from "fabric/fabric-impl";

const iconMap: { [key: string]: string } = {
    'door': './icons/singleDoorIcon.png',
    'door-double': './icons/doubleDoorIcon.png',
    'window': './icons/windowIcon.png'
}
const doubleDoorTypes = ['Double Folding', 'Bypass', 'Double Hinged', 'Patio', 'Double Pocket'];
export interface TwoDWindowDoorRendererProps {
    canvas: fabric.Canvas,
    wallContainerRect: fabric.Object;
    mesh: TwoDMesh,
    wallId: string,
    unitsScale: number,
    isActive: boolean,
    xOffset: number,
    yOffset: number
}

const TwoDWindowDoorRenderer: React.FC<TwoDWindowDoorRendererProps> = ({ canvas, wallContainerRect, mesh, wallId, unitsScale, isActive, xOffset, yOffset }
) => {
    const dispatch = useDispatch();
    const windowOrDoorRect = useRef<fabric.Object | undefined>(undefined);
    const [windowOrDoorImgEle, setWindowOrDoorImgEle] = useState<HTMLImageElement | undefined>(undefined);
    const prevRectScaleX = useRef(0);
    const prevRectLeft = useRef(0);
    const prevRectTop = useRef(0);
    let xAdjustment = wallId === 'wall1' ? 20 : wallId === 'wall3' ? -18 : 0;
    let yAdjustment = wallId === 'wall0' ? -19 : wallId === 'wall2' ? 19 : 0;
    const posX = (mesh.position.x - xOffset) * unitsScale;
    const posY = (mesh.position.y - yOffset) * unitsScale;
    if (mesh.type === 'window') xAdjustment = yAdjustment = 0;

    const onRectModified = () => {
        dispatch(builderActions.updateTwoDWallMeshChild({
            wallId: wallId,
            childId: mesh.id,
            position: {
                x: (windowOrDoorRect.current!.left! + xOffset * unitsScale - xAdjustment) / unitsScale,
                y: (windowOrDoorRect.current!.top! + yOffset * unitsScale - yAdjustment) / unitsScale
            },
            width: windowOrDoorRect.current!.getScaledWidth() / unitsScale,
            undoable: true
        }));
    };

    const onMeshSelected = useCallback(() => {
        dispatch(builderActions.updateTwoDUi({
            showWallCtxMenu: false
        }));
        dispatch(builderActions.updateActiveWallId({
            activeWallId: wallId
        }));
        dispatch(builderActions.updateActiveWindowOrDoorId({
            activeWindowOrDoorId: mesh.id
        }));
        dispatch(builderActions.updateActiveSelection({
            screenCoords: {
                x: posX - (mesh.width * unitsScale) / 2 + canvas.viewportTransform![4] || 0,
                y: windowOrDoorRect.current!.getBoundingRect().top
            }
        }));
    }, []);

    const onMeshDeselected = useCallback(() => {
        dispatch(builderActions.updateActiveWindowOrDoorId({
            activeWindowOrDoorId: ''
        }));
    }, []);

    const onRectScaling = useCallback(() => {
        prevRectScaleX
        if (wallId === 'wall0' || wallId === 'wall2') {
            const touchesLeft = windowOrDoorRect.current!.left! - windowOrDoorRect.current!.getScaledWidth() / 2 <= wallContainerRect.left! - wallContainerRect.width! / 2 + 20;
            const touchesRight = windowOrDoorRect.current!.left! + windowOrDoorRect.current!.getScaledWidth() / 2 >= wallContainerRect.left! + wallContainerRect.width! / 2 - 20;
            if (touchesLeft || touchesRight) {
                windowOrDoorRect.current!.scaleX = prevRectScaleX.current;
                windowOrDoorRect.current!.left = prevRectLeft.current;
            }
            prevRectLeft.current = windowOrDoorRect.current!.left!;
        }

        if (wallId === 'wall1' || wallId === 'wall3') {
            // console.log('debug:: scaling...',
            //     windowOrDoorRect.current!.top!,
            //     wallContainerRect.top,
            //     windowOrDoorRect.current!.top! - windowOrDoorRect.current!.getScaledWidth() / 2,
            //     wallContainerRect.top! - wallContainerRect.width! / 2,
            //     windowOrDoorRect.current!.top! + windowOrDoorRect.current!.getScaledWidth() / 2,
            //     wallContainerRect.top! + wallContainerRect.width! / 2);
            const touchesTop = windowOrDoorRect.current!.top! - windowOrDoorRect.current!.getScaledWidth() / 2 <= wallContainerRect.top! - wallContainerRect.width! / 2 + 20;
            const touchesBottom = windowOrDoorRect.current!.top! + windowOrDoorRect.current!.getScaledWidth() / 2 >= wallContainerRect.top! + wallContainerRect.width! / 2 - 20;
            if (touchesTop || touchesBottom) {
                windowOrDoorRect.current!.scaleX = prevRectScaleX.current;
                windowOrDoorRect.current!.top = prevRectTop.current;
            }
            prevRectTop.current = windowOrDoorRect.current!.top!;
        }
        prevRectScaleX.current = windowOrDoorRect.current!.scaleX!;
    }, []);

    // Function to check if a point is inside a polygon
    function pointInPolygon(point: Point, polygon: Point[]) {
        const { x, y } = point;
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i].x, yi = polygon[i].y;
            const xj = polygon[j].x, yj = polygon[j].y;

            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    }

    // Get the corners of a rotated rectangle
    function getCorners(rect: any) {
        const matrix = rect.calcTransformMatrix();
        const tl = fabric.util.transformPoint({ x: -rect.width! / 2, y: -rect.height! / 2 } as Point, matrix);
        const tr = fabric.util.transformPoint({ x: rect.width! / 2, y: -rect.height! / 2 } as Point, matrix);
        const br = fabric.util.transformPoint({ x: rect.width! / 2, y: rect.height! / 2 } as Point, matrix);
        const bl = fabric.util.transformPoint({ x: -rect.width / 2, y: rect.height! / 2 } as Point, matrix);
        //return [tl, tr, br, bl];
        return [
            rect.lineCoords!.tl,
            rect.lineCoords!.tr,
            rect.lineCoords!.br,
            rect.lineCoords!.bl
        ]
    }

    function adjustPosition(innerRect: any, outerRect: any) {
        const outerCorners = getCorners(outerRect);
        const innerCorners = getCorners(innerRect);

        const allInside = innerCorners.every(corner => pointInPolygon(corner, outerCorners));

        if (!allInside) {
            const wallEndPoints = findEndpoints(outerRect.left, outerRect.top, outerRect.angle, outerRect.width);
            const childEndpoints = findEndpoints(innerRect.left, innerRect.top, innerRect.angle, mesh.width * unitsScale);
            const middlePerpendicularLine = findEndpoints(innerRect.left, innerRect.top, (outerRect.angle + 90) % 180, 800);
            const middleIntersectPoint = intersectOfLines(
                wallEndPoints.endpoint1.x,
                wallEndPoints.endpoint1.y,
                wallEndPoints.endpoint2.x,
                wallEndPoints.endpoint2.y,
                middlePerpendicularLine.endpoint1.x,
                middlePerpendicularLine.endpoint1.y,
                middlePerpendicularLine.endpoint2.x,
                middlePerpendicularLine.endpoint2.y
            );

            let childPoint1, childPoint2;
            if (wallId === 'wall0' || wallId === 'wall2') {
                if (innerRect.angle >= 90) {
                    childPoint1 = childEndpoints.endpoint2;
                    childPoint2 = childEndpoints.endpoint1;
                } else {
                    childPoint1 = childEndpoints.endpoint1;
                    childPoint2 = childEndpoints.endpoint2;
                }
                console.log('intersectPoint :: ', innerRect.angle, outerRect.angle, childPoint1.x, childPoint2.x, wallEndPoints.endpoint1.x, wallEndPoints.endpoint2.x);
                if (childPoint1.x < wallEndPoints.endpoint1.x) {
                    console.log('intersectPoint:: using left edge ');
                    const tempPoints = findEndpoints(wallEndPoints.endpoint1.x, wallEndPoints.endpoint1.y, outerRect.angle, mesh.width * unitsScale);
                    innerRect.set('left', tempPoints.endpoint2.x + xAdjustment);
                    innerRect.set('top', tempPoints.endpoint2.y + yAdjustment);
                }
                else if (childPoint2.x > wallEndPoints.endpoint2.x) {
                    console.log('intersectPoint:: using right edge ');
                    const tempPoints = findEndpoints(wallEndPoints.endpoint2.x, wallEndPoints.endpoint2.y, outerRect.angle, mesh.width * unitsScale);
                    innerRect.set('left', tempPoints.endpoint1.x + xAdjustment);
                    innerRect.set('top', tempPoints.endpoint1.y + yAdjustment);
                }
                else if (middleIntersectPoint) {
                    innerRect.set('left', middleIntersectPoint.x + xAdjustment);
                    innerRect.set('top', middleIntersectPoint.y + yAdjustment);
                }
            }

            if (wallId === 'wall1' || wallId === 'wall3') {
                if (outerRect.angle < 0) {
                    if (innerRect.angle >= 180) {
                        childPoint1 = childEndpoints.endpoint1;
                        childPoint2 = childEndpoints.endpoint2;
                    } else {
                        childPoint1 = childEndpoints.endpoint2;
                        childPoint2 = childEndpoints.endpoint1;
                    }
                    console.log('intersectPoint :: ', innerRect.angle, outerRect.angle, childPoint1.y, childPoint2.y, wallEndPoints.endpoint1.y, wallEndPoints.endpoint2.y);
                    if (childPoint1.y > wallEndPoints.endpoint1.y) {
                        console.log('intersectPoint:: using top edge ');
                        const tempPoints = findEndpoints(wallEndPoints.endpoint1.x, wallEndPoints.endpoint1.y, outerRect.angle, mesh.width * unitsScale);
                        innerRect.set('left', tempPoints.endpoint2.x + xAdjustment);
                        innerRect.set('top', tempPoints.endpoint2.y + yAdjustment);
                    }
                    else if (childPoint2.y < wallEndPoints.endpoint2.y) {
                        console.log('intersectPoint:: using bottom edge ');
                        const tempPoints = findEndpoints(wallEndPoints.endpoint2.x, wallEndPoints.endpoint2.y, outerRect.angle, mesh.width * unitsScale);
                        innerRect.set('left', tempPoints.endpoint1.x + xAdjustment);
                        innerRect.set('top', tempPoints.endpoint1.y + yAdjustment);
                    }
                    else if (middleIntersectPoint) {
                        innerRect.set('left', middleIntersectPoint.x + xAdjustment);
                        innerRect.set('top', middleIntersectPoint.y + yAdjustment);
                    }
                }
                if (outerRect.angle > 0) {
                    if (innerRect.angle >= 180) {
                        childPoint1 = childEndpoints.endpoint2;
                        childPoint2 = childEndpoints.endpoint1;
                    } else {
                        childPoint1 = childEndpoints.endpoint1;
                        childPoint2 = childEndpoints.endpoint2;
                    }
                    console.log('intersectPoint :: ', innerRect.angle, outerRect.angle, childPoint1.y, childPoint2.y, wallEndPoints.endpoint1.y, wallEndPoints.endpoint2.y);
                    if (childPoint1.y < wallEndPoints.endpoint1.y) {
                        console.log('intersectPoint:: using top edge ');
                        const tempPoints = findEndpoints(wallEndPoints.endpoint1.x, wallEndPoints.endpoint1.y, outerRect.angle, mesh.width * unitsScale);
                        innerRect.set('left', tempPoints.endpoint2.x + xAdjustment);
                        innerRect.set('top', tempPoints.endpoint2.y + yAdjustment);
                    }
                    else if (childPoint2.y > wallEndPoints.endpoint2.y) {
                        console.log('intersectPoint:: using bottom edge ');
                        const tempPoints = findEndpoints(wallEndPoints.endpoint2.x, wallEndPoints.endpoint2.y, outerRect.angle, mesh.width * unitsScale);
                        innerRect.set('left', tempPoints.endpoint1.x + xAdjustment);
                        innerRect.set('top', tempPoints.endpoint1.y + yAdjustment);
                    }
                    else if (middleIntersectPoint) {
                        innerRect.set('left', middleIntersectPoint.x + xAdjustment);
                        innerRect.set('top', middleIntersectPoint.y + yAdjustment);
                    }
                }
            }
        }
    }
    const onRectMoving = useCallback(() => {
        adjustPosition(windowOrDoorRect.current, wallContainerRect);
    }, [])
    const initEvents = (currentRect: fabric.Object) => {
        currentRect.on('modified', onRectModified);
        currentRect.on('selected', onMeshSelected);
        currentRect.on('deselected', onMeshDeselected);
        currentRect.on('scaling', onRectScaling);
        currentRect.on('moving', onRectMoving);
    }

    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setWindowOrDoorImgEle(img);
        }
        const doubleStr = mesh.type === 'door' && doubleDoorTypes.indexOf(mesh.subType!) !== -1 ? '-double' : '';
        img.src = iconMap[mesh.type as string + doubleStr];
    }, []);


    let imgAngle = wallId === 'wall0' ? 0 : wallId === 'wall2' ? 180 : 0;
    imgAngle = wallId === 'wall1' && mesh.rotation ? 0 : wallId === 'wall2' ? 180 : 0;
    if (wallId === 'wall2') imgAngle = 180;
    if (wallId === 'wall1') {
        if (mesh.rotation < 0) imgAngle = 180;
    }
    if (wallId === 'wall3') {
        if (mesh.rotation > 0) imgAngle = 180;
        if (mesh.rotation < 0) imgAngle = 360;
    }
    windowOrDoorRect.current = useMemo(() => {
        if (!windowOrDoorImgEle) return undefined;
        const obj = new fabric.Rect({
            //left: mesh.position.x * unitsScale,
            //top: mesh.position.y * unitsScale,
            //@ts-ignore
            rectType: mesh.type,
            //@ts-ignore
            rectId: mesh.id,
            //@ts-ignore
            wallId: wallId,
            originX: 'center',
            originY: 'center',
            fill: mesh.type === 'door' ? 'white' : 'white',
            height: 20,
            width: mesh.width * unitsScale,
            angle: mesh.rotation,
            lockMovementX: false,
            lockMovementY: false,
            lockRotation: true,
            opacity: 1,
            cornerColor: '#0D908E',
            transparentCorners: false,
            cornerStyle: 'circle',
            cornerSize: 10
        });
        //canvas.add(obj);

        const imgObj = new fabric.Image(windowOrDoorImgEle!, {
            name: 'window_door_' + mesh.id,
            left: posX + xAdjustment,
            top: posY * unitsScale + yAdjustment,
            originX: 'center',
            originY: 'center',
            scaleY: 0.4,
            scaleX: (mesh.width * unitsScale) / windowOrDoorImgEle.width!,
            angle: imgAngle + mesh.rotation,
            cornerColor: '#0D908E',
            transparentCorners: false,
            cornerStyle: 'circle',
            cornerSize: 10,
            //hasControls: mesh.type === 'window'
        });
        canvas.add(imgObj);
        initEvents(imgObj);
        return imgObj;
    }, [windowOrDoorImgEle]);

    if (windowOrDoorRect.current) {
        windowOrDoorRect.current.left = posX + xAdjustment;
        windowOrDoorRect.current.top = posY + yAdjustment;
        //windowOrDoorRect.current.width = (mesh.width * unitsScale) / windowOrDoorRect.current.scaleX!;
        windowOrDoorRect.current.angle = imgAngle + mesh.rotation;
        // windowOrDoorRect.current.setControlsVisibility({
        //     tr: false,
        //     bl: false,
        //     ml: true,
        //     mt: false,
        //     mr: true,
        //     mb: false,
        //     mtr: false
        // });
        windowOrDoorRect.current.setControlVisible('tr', false);
        windowOrDoorRect.current.setControlVisible('tl', false);
        windowOrDoorRect.current.setControlVisible('br', false);
        windowOrDoorRect.current.setControlVisible('bl', false);
        windowOrDoorRect.current.setControlVisible('mt', false);
        windowOrDoorRect.current.setControlVisible('mb', false);
        windowOrDoorRect.current.setControlVisible('mtr', false);
        windowOrDoorRect.current.setCoords();
        if (isActive)
            canvas.setActiveObject(windowOrDoorRect.current);
        canvas.requestRenderAll();
    }
    return <></>;
}

export default TwoDWindowDoorRenderer;